import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type {
  PageResponse,
  ProjectSummary,
  ReleaseCustomFieldDefinition,
} from "../../shared/types/api";
import {
  Button,
  DataTable,
  InlineAlert,
  PageHeader,
  Select,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import { RELEASE_TYPE_LABELS } from "../releases/releaseVocabulary";
import { ReleaseFieldDrawer } from "./ReleaseFieldDrawer";

const TYPE_LABELS: Record<string, string> = {
  TEXT: "Text",
  TEXT_AREA: "Text area",
  NUMBER: "Number",
  DATE: "Date",
  BOOLEAN: "Yes / no",
  SELECT: "List",
  URL: "Link",
};

/**
 * The extra questions an organisation asks of its releases (§58-§61).
 *
 * <p>Scoped either to one project or to all of them. A compliance field — a CAB reference, a
 * regulatory classification — almost always belongs everywhere, and making somebody add it to
 * each project separately means it is quietly missing from the next one somebody creates.
 *
 * <p>Withdrawn fields are listed rather than hidden. Releases that already answered them still
 * hold those answers, and a list that dropped the definition would leave somebody wondering what
 * the value on an old release was ever for.
 */
export function ReleaseFieldsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<string>("");
  const [editing, setEditing] = useState<ReleaseCustomFieldDefinition | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const projects = useQuery({
    queryKey: ["projects", "picker"],
    queryFn: () =>
      api.get<PageResponse<ProjectSummary>>("/api/v1/projects?size=200&sort=name,asc"),
    staleTime: 5 * 60 * 1000,
  });

  const fields = useQuery({
    queryKey: ["release-fields", scope],
    queryFn: () =>
      api.get<ReleaseCustomFieldDefinition[]>(
        scope === ""
          ? "/api/v1/release-fields"
          : `/api/v1/release-fields?projectId=${scope}`,
      ),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["release-fields"] });
  };

  const setOffered = async (field: ReleaseCustomFieldDefinition, offered: boolean) => {
    setBusy(field.id);
    setError(null);
    try {
      await api.post(
        `/api/v1/release-fields/${field.id}/${offered ? "reinstate" : "withdraw"}`,
      );
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not do that.");
    } finally {
      setBusy(null);
    }
  };

  const columns: Column<ReleaseCustomFieldDefinition>[] = [
    {
      key: "label",
      header: "Field",
      render: (field) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{field.label}</p>
          <p className="font-mono text-xs text-ink-400">{field.fieldKey}</p>
        </div>
      ),
    },
    {
      key: "fieldType",
      header: "Answer",
      render: (field) => (
        <span className="text-sm text-ink-700">
          {TYPE_LABELS[field.fieldType] ?? field.fieldType}
          {field.fieldType === "SELECT" && field.options.length > 0 && (
            <span className="ml-1 text-xs text-ink-500">({field.options.length} options)</span>
          )}
        </span>
      ),
    },
    {
      key: "required",
      header: "Required",
      render: (field) =>
        field.required ? (
          <StatusBadge tone="amber">Holds the release</StatusBadge>
        ) : (
          <span className="text-ink-400">Optional</span>
        ),
    },
    {
      key: "appliesTo",
      header: "Asked of",
      render: (field) =>
        field.appliesTo ? (
          <StatusBadge tone="gray">{RELEASE_TYPE_LABELS[field.appliesTo]}</StatusBadge>
        ) : (
          <span className="text-ink-500">Every release</span>
        ),
    },
    {
      key: "displayOrder",
      header: "Order",
      render: (field) => <span className="tnum">{field.displayOrder}</span>,
    },
    {
      key: "active",
      header: "Status",
      render: (field) => (
        <StatusBadge tone={field.active ? "green" : "gray"}>
          {field.active ? "Asked" : "Withdrawn"}
        </StatusBadge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (field) =>
        can("RELEASE_FIELD_MANAGE") ? (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              loading={busy === field.id}
              onClick={(event) => {
                event.stopPropagation();
                void setOffered(field, !field.active);
              }}
            >
              {field.active ? "Withdraw" : "Ask again"}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Release fields"
        subtitle="Extra details your organisation records against every release — a CAB reference, a classification, a contract number."
        actions={
          can("RELEASE_FIELD_MANAGE") && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
              New field
            </Button>
          )
        }
      />

      <div className="mb-3 flex items-end gap-3">
        <div className="w-72">
          <label className="mb-1 block text-sm font-medium text-ink-700" htmlFor="field-scope">
            Where it applies
          </label>
          <Select
            id="field-scope"
            value={scope}
            onChange={(event) => setScope(event.target.value)}
          >
            <option value="">Every project</option>
            {(projects.data?.content ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {error && (
        <div className="mb-3">
          <InlineAlert tone="error">{error}</InlineAlert>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={fields.data ?? []}
        rowKey={(field) => field.id}
        loading={fields.isPending}
        onRowClick={can("RELEASE_FIELD_MANAGE") ? setEditing : undefined}
        emptyTitle="No fields here"
        emptyMessage={
          scope === ""
            ? "Fields defined here are asked of every release in every project. A required one fails the readiness gate by name until it is answered."
            : "Nothing specific to this project. Fields that apply everywhere are under Every project."
        }
        emptyAction={
          can("RELEASE_FIELD_MANAGE") ? (
            <Button onClick={() => setAdding(true)}>Define the first field</Button>
          ) : undefined
        }
      />

      <ReleaseFieldDrawer
        field={editing}
        projectId={scope === "" ? null : scope}
        open={adding || editing != null}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={refresh}
      />
    </>
  );
}
